import { z } from "zod";
import { groupCategoryEmbeddedSchema } from "./groups.js";

/**
 * Shared DTO cho Admin Group Directory & Detail (US-AE4.F1-0015 / @UC9030 group-directory · @UC9031 group-fund-detail).
 * Một nguồn sự thật cho api (S01 — query + redaction theo vai) và admin web (S03 — render),
 * tránh drift shape ở 2 nơi (DRY).
 *
 * BẢO MẬT (invariants):
 * - C2: field PII (ownerName/displayName) NULLABLE+OPTIONAL — backend redact theo vai (MASK) hoặc
 *   placeholder (anonymized → null). Contract CHỈ cho phép vắng/null, KHÔNG ép lộ.
 * - B1: tiền = BigInt minor units serialize thành STRING, không float.
 * - Field redact-được (totalBalanceMinor/balanceMinor) = nullable — khi vai thấp không được xem.
 * - `.strict()` — KHÔNG khai báo field lạ; field lạ bị reject tại biên (chống vô tình leak).
 * - Cursor opaque string — đối xứng common/pagination/cursor.
 *
 * Envelope khớp quy ước list repo: `{ data, pageInfo: { hasNext, nextCursor } }`
 * (common/pagination/cursor.ts · admin-search). KHÔNG dùng {items, nextCursor}.
 *
 * Limit: default 20 (backend áp khi vắng), max 50. q OPTIONAL (vắng ⇒ list toàn bộ theo cursor).
 */

/** Max page-size. Default (20) do backend áp khi vắng. */
export const ADMIN_GROUP_DIRECTORY_MAX_LIMIT = 50;

/** Độ dài tối đa của `q` — chặn input phình to tại biên. 256 dư cho tên nhóm/uuid. */
export const ADMIN_GROUP_DIRECTORY_MAX_QUERY_LENGTH = 256;

/**
 * 🔴 US-AE16.F2-0370-S01 (@UC9634) — bộ lọc VÒNG ĐỜI nhóm.
 *
 * ⚠️ Tên là `state`, **KHÔNG** phải `status` — cố ý. `archived` KHÔNG phải giá trị của cột
 * `groups.status` (CHECK chỉ cho `active|suspended`); nó là `archivedAt IS NOT NULL`. Đặt trùng tên
 * `status` sẽ khiến người đọc tưởng `?status=archived` chạy được rồi ngạc nhiên vì 400 — và tệ hơn,
 * khiến người viết service tưởng chỉ cần so cột `status` là xong.
 *
 * `active` = `status='active' AND archivedAt IS NULL` — nhóm đã đóng sổ mà `status` vẫn `active`
 * KHÔNG được tính là đang hoạt động (nếu bỏ vế `archivedAt IS NULL` thì màn lọc nói dối).
 */
export const GROUP_STATE_FILTERS = ["active", "suspended", "archived"] as const;

export const groupStateFilterSchema = z.enum(GROUP_STATE_FILTERS);

export type GroupStateFilter = z.infer<typeof groupStateFilterSchema>;

/**
 * Query list: `q` OPTIONAL (tên nhóm, ≤256), `state` OPTIONAL (vòng đời), cursor opaque optional,
 * limit ∈ [1,50] optional. Vắng `q`/`state` ⇒ liệt kê toàn bộ (cursor) — 0 REGRESS.
 */
export const adminGroupDirectoryQuerySchema = z.object({
  q: z.string().max(ADMIN_GROUP_DIRECTORY_MAX_QUERY_LENGTH).optional(),
  state: groupStateFilterSchema.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(ADMIN_GROUP_DIRECTORY_MAX_LIMIT).optional(),
  /**
   * US-AE16.F7-0434-S01 (`@UC9032`) — hiện cả nhóm đã XOÁ MỀM (`deletedAt`).
   *
   * 🔴 Trục KHÁC `state` ngay bên trên: `state` lọc theo `status` (active/suspended), cờ này lọc
   * theo `deletedAt`. Một nhóm có thể vừa `suspended` vừa `deleted` — gộp chúng thành một enum
   * "trạng thái nhóm" là mất khả năng biểu diễn tổ hợp có thật đó.
   *
   * Vắng ⇒ `false` ⇒ **hành vi cũ y nguyên**. RBAC (SUPER_ADMIN-only) gác ở controller.
   */
  includeDeleted: z.coerce.boolean().optional(),
});

export type AdminGroupDirectoryQuery = z.infer<typeof adminGroupDirectoryQuerySchema>;

/**
 * List item (UC9030 AC1). ownerName/lastActivityAt NULLABLE+OPTIONAL (redaction/group mới).
 * totalBalanceMinor = STRING (BigInt minor) hoặc NULL (redact theo vai) — KHÔNG number float (B1).
 * KHÔNG bao giờ chứa password/secret (`.strict()`).
 */
export const adminGroupDirectoryItemSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    ownerName: z.string().nullable().optional(),
    memberCount: z.number().int().min(0),
    fundCount: z.number().int().min(0),
    /** BigInt minor units as string; null when redacted for lower-privilege roles. KHÔNG number. */
    totalBalanceMinor: z.string().nullable(),
    currency: z.string(),
    createdAt: z.string(),
    lastActivityAt: z.string().nullable().optional(),
    /**
     * 🔴 US-AE16.F2-0370-S01 (@UC9634) — VÒNG ĐỜI nhóm ở LIST (trước chỉ detail mới có `status`).
     *
     * KHÔNG redact theo vai: đây không phải tiền, và là thứ SUPPORT cần NHẤT để trả lời
     * *"sao nhóm tôi đóng sổ rồi mà vẫn bị đòi tiền?"* — câu hỏi sinh ra từ bug #6 (worker vẫn đẻ
     * `member_dues` cho nhóm đã archive/suspend).
     *
     * `status` = cột `groups.status` (`active|suspended`). `archivedAt` là trục ĐỘC LẬP: nhóm đã
     * đóng sổ vẫn có thể mang `status='active'`.
     */
    status: z.enum(["active", "suspended"]),
    archivedAt: z.string().nullable(),
  })
  .strict();

export type AdminGroupDirectoryItem = z.infer<typeof adminGroupDirectoryItemSchema>;

/** Envelope khớp quy ước repo `{ data, pageInfo }` (cursor opaque, hasNext). */
export const adminGroupDirectoryResponseSchema = z.object({
  data: z.array(adminGroupDirectoryItemSchema),
  pageInfo: z.object({
    hasNext: z.boolean(),
    nextCursor: z.string().nullable(),
  }),
});

export type AdminGroupDirectoryResponse = z.infer<typeof adminGroupDirectoryResponseSchema>;

/**
 * Fund item trong group detail (UC9031 AC2).
 * balanceMinor = STRING (BigInt) hoặc NULL (redact theo vai) — KHÔNG number float (B1).
 */
export const adminGroupFundItemSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    currency: z.string(),
    /** BigInt minor units as string; null when redacted for lower-privilege roles. KHÔNG number. */
    balanceMinor: z.string().nullable(),
    isArchived: z.boolean(),
  })
  .strict();

export type AdminGroupFundItem = z.infer<typeof adminGroupFundItemSchema>;

/**
 * Member item trong group detail (UC9031 AC2).
 * displayName NULLABLE (redact PII theo vai).
 * userId (US-AE-IMP2-T09 · @UC9152): platform user id — CON TRỎ điều hướng (link admin → /users/:id).
 *   KHÔNG phải PII → hiện cho MỌI vai (đồng nhất groupMemberId). NULLABLE cho member chưa gắn user thật.
 */
export const adminGroupMemberItemSchema = z
  .object({
    groupMemberId: z.string(),
    userId: z.string().nullable(),
    role: z.string(),
    status: z.string(),
    displayName: z.string().nullable(),
    /**
     * US-E23.F16-0348-S01 (@UC9543) — **tổng đã đóng** của thành viên, string minor (B1, no float).
     *
     * VÌ SAO THÊM: client đã có `totalContributedMinor` từ 0331 (`groups.ts:451-453`) nhưng admin thì
     * không ⇒ bất đối xứng **người dùng thấy số, người hỗ trợ họ thì không**; OPS xử khiếu nại
     * *"tôi đóng rồi mà vẫn bị nhắc"* phải vào DB.
     *
     * 🔴 CHỈ cộng trạng thái ĐÃ XÁC NHẬN NHẬN (`CONTRIBUTION_COUNTED_STATUSES`), **không** cộng
     * `awaiting_confirmation` — §3.7: gộp (2) *"đã chuyển"* vào (3) *"đã nhận"* = **sổ ảo**.
     *
     * 🔴 `null` = **redact theo vai** (SUPPORT/READ_ONLY), KHÔNG phải "chưa có dữ liệu". Quy ước
     * redaction của lane admin là `null` (mirror `admin-collections.ts` `totalMinorString`), **không**
     * `'•••'` như lane client — nhưng tuyệt đối không phải `0`: số 0 nói dối rằng "đã đóng 0 đồng".
     * `"0"` và `null` PHẢI phân biệt được ở cả API lẫn UI.
     *
     * `.optional()` là **shim rolling-deploy**, không phải "server được phép quên": FE zod-parse cả
     * response detail, nên nếu bản FE mới gặp bản API cũ mà field bắt buộc thì **vỡ cả màn** chứ không
     * chỉ mất một ô. Server LUÔN set — có test API chứng minh.
     *
     * 🔴 KHÔNG kèm `currency` ở từng dòng member: một nhóm chỉ có MỘT đơn vị tiền
     * (`Group.defaultCurrency`, bị cấm PATCH ở `group-lifecycle.service.ts:109`). Nhét currency vào
     * mỗi dòng là đẻ N nguồn cho một sự thật — currency nằm ở cấp group (dưới).
     */
    totalContributedMinor: z
      .string()
      .regex(/^-?\d+$/, "totalContributedMinor phải là số nguyên minor units (không float)")
      .nullable()
      .optional(),
    /**
     * US-AE4.F6-0856 (@UC9871 · QĐ-14) — suất này có nhận được vai quản lý (Quản trị / Thủ quỹ / Chủ
     * nhóm) không: `eligible` = có người đứng tên tài khoản chính còn hoạt động · `no_primary_account` =
     * chỉ có phụ huynh gắn thêm / chưa ai nhận (app chỉ trao quyền cho người đứng tên chính) ·
     * `account_inactive` = tài khoản chính đã xoá hoặc tạm khoá. Để form hiện mờ lựa chọn kèm lý do;
     * máy chủ VẪN kiểm lại ở mọi lần bấm. `.optional()` = shim rolling-deploy.
     */
    roleEligibility: z.enum(["eligible", "no_primary_account", "account_inactive"]).optional(),
  })
  .strict();

export type AdminGroupMemberItem = z.infer<typeof adminGroupMemberItemSchema>;

/**
 * Config override item — diff `groups.settings`/`funds.settings` so SystemSetting.defaultValue (UC9031 AC2).
 */
export const adminGroupConfigOverrideItemSchema = z
  .object({
    key: z.string(),
    groupValue: z.string(),
    defaultValue: z.string(),
  })
  .strict();

export type AdminGroupConfigOverrideItem = z.infer<typeof adminGroupConfigOverrideItemSchema>;

/**
 * Group detail 360° (UC9031 AC2). Nested mảng BẮT BUỘC có (rỗng khi không có).
 * KHÔNG field secret (`.strict()`).
 */
export const adminGroupDetailSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    // US-AE4.F3-0016 (@UC9032): trạng thái vòng đời nhóm (active | suspended) — UI quyết định
    // hiện nút Suspend vs Restore + banner freeze. Backfill 'active' cho nhóm cũ (S04).
    status: z.enum(["active", "suspended"]),
    createdAt: z.string(),
    /**
     * 🔴 US-AE16.F2-0370-S01 (@UC9634) — mốc đóng sổ, cùng một sự thật với list (không để hai màn
     * nói hai chuyện). `null` = chưa đóng sổ. KHÔNG redact (không phải tiền).
     */
    archivedAt: z.string().nullable(),
    funds: z.array(adminGroupFundItemSchema),
    members: z.array(adminGroupMemberItemSchema),
    /**
     * US-E23.F16-0348-S01 (@UC9544) — đơn vị tiền của NHÓM (`Group.defaultCurrency`), ISO 4217.
     * 🔴 Đặt ở cấp group vì mỗi nhóm chỉ có MỘT đơn vị tiền: `Fund.currency` kế thừa
     * `Group.defaultCurrency` lúc tạo (`funds.service.ts:385`) và `defaultCurrency` **bị cấm PATCH**
     * (`group-lifecycle.service.ts:109` — *"đổi đơn vị tiền của nhóm đã có sổ cái"*). ⇒ không thể có
     * hai currency trong một nhóm, nên không nhân bản field này xuống từng quỹ/từng member.
     * `.optional()` = shim rolling-deploy (xem `totalContributedMinor`).
     */
    currency: z.string().length(3).optional(),
    /**
     * US-AE4.F6-0857 (@UC9875) — mô tả nhóm (`Group.purpose`) để form "Sửa thông tin nhóm" điền sẵn.
     * `null` = chưa có mô tả. `.optional()` = shim rolling-deploy.
     */
    purpose: z.string().nullable().optional(),
    /**
     * US-AE4.F6-0859 (@UC9945 · QĐ-10) — LUẬT của CHÍNH nhóm (`groups.type`): `parent` ⇒ bảng suất có cột
     * + mục Người đại diện. Tách khỏi `category` vì nhóm parent có thể CHƯA gán danh mục (`category = null`)
     * mà vẫn là nhóm phụ huynh. `.optional()` = shim rolling-deploy.
     */
    type: z.enum(["standard", "parent"]).optional(),
    /**
     * US-AE4.F6-0859 (@UC9945) — danh mục + danh từ theo danh mục (`seat_noun` / `representative_noun`), dựng
     * bằng MỘT cửa `buildGroupCategoryEmbedded` như ba response của app. `null` = chưa phân loại.
     * `.optional()` = shim rolling-deploy.
     */
    category: groupCategoryEmbeddedSchema.nullable().optional(),
    configOverride: z.array(adminGroupConfigOverrideItemSchema),
  })
  .strict();

export type AdminGroupDetail = z.infer<typeof adminGroupDetailSchema>;
